import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, Lock } from 'lucide-react';

const SecurePDFViewer = ({ pdfUrl, userName, userEmail }) => {
  const [scale, setScale] = useState(100);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    // Disable right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable text selection
    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable print
    const handleBeforePrint = (e) => {
      e.preventDefault();
      alert('L\'impression est désactivée pour protéger ce document.');
      return false;
    };

    // Disable keyboard shortcuts (Ctrl+P, Ctrl+S, Ctrl+C)
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'p' || e.key === 's' || e.key === 'c') {
          e.preventDefault();
          if (e.key === 'p') {
            alert('L\'impression est désactivée pour protéger ce document.');
          }
          return false;
        }
      }
      // Disable PrtScn
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        return false;
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('contextmenu', handleContextMenu);
      container.addEventListener('selectstart', handleSelectStart);
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('beforeprint', handleBeforePrint);
    }

    return () => {
      if (container) {
        container.removeEventListener('contextmenu', handleContextMenu);
        container.removeEventListener('selectstart', handleSelectStart);
      }
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('beforeprint', handleBeforePrint);
    };
  }, []);

  const zoomIn = () => setScale(prev => Math.min(prev + 10, 150));
  const zoomOut = () => setScale(prev => Math.max(prev - 10, 50));

  return (
    <div className="flex flex-col h-full bg-gray-100" ref={containerRef}>
      {/* Security Notice */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex items-center">
          <Lock className="w-5 h-5 text-yellow-700 mr-2" />
          <div className="text-sm text-yellow-700">
            <p className="font-medium">Document protégé</p>
            <p className="text-xs mt-1">
              Ce document est protégé contre la copie, le téléchargement et l'impression. 
              Visible uniquement par {userName} ({userEmail})
            </p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-gray-800 text-white p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Visualiseur PDF Sécurisé</span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomOut}
            disabled={scale <= 50}
            className="text-white hover:bg-gray-700"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-sm">{scale}%</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomIn}
            disabled={scale >= 150}
            className="text-white hover:bg-gray-700"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 overflow-hidden bg-gray-200 relative select-none">
        {/* Watermark Overlay */}
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-10">
          <div 
            className="transform rotate-[-45deg] text-gray-400 opacity-10 text-6xl font-bold select-none"
            style={{ userSelect: 'none', pointerEvents: 'none' }}
          >
            <div className="text-center">
              {userName}<br/>{userEmail}
            </div>
          </div>
        </div>
        
        {/* PDF Iframe */}
        <iframe
          ref={iframeRef}
          src={pdfUrl}
          className="w-full h-full border-0"
          style={{
            transform: `scale(${scale / 100})`,
            transformOrigin: 'top center',
            height: `${100 / (scale / 100)}%`
          }}
          title="PDF Viewer"
        />
      </div>

      {/* Footer Notice */}
      <div className="bg-gray-800 text-gray-400 text-xs p-2 text-center">
        Document confidentiel - SFOALC © {new Date().getFullYear()} - Tous droits réservés
      </div>
    </div>
  );
};

export default SecurePDFViewer;
